import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from './auth.service';
import { PASSWORD_POLICY_HINT, passwordPolicyValidator } from './password-policy';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  template: `
    <div class="auth-container">
      <div class="scryber-icon-row">
        <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
      </div>
      <div class="auth-card">
        <a routerLink="/login" class="back-link">← Back</a>
        <div class="auth-header">
          <h1>Set new password</h1>
          <p class="auth-subtitle">Enter your new password below.</p>
        </div>

        @if (tokenMissing) {
          <div class="auth-error" role="alert">
            Invalid or expired reset link. Please request a new one from the
            <a routerLink="/forgot-password" class="auth-link">forgot password</a> page.
          </div>
        } @else if (errorMessage) {
          <div class="auth-error" role="alert">
            {{ errorMessage }}
          </div>
        }

        @if (!tokenMissing) {
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="auth-form">
            <div class="form-group">
              <label for="newPassword">New password <span class="required">*</span></label>
              <input id="newPassword" type="password" formControlName="newPassword" placeholder="••••••••" autocomplete="new-password" />
              @if (form.get('newPassword')?.touched) {
                @if (form.get('newPassword')?.hasError('required')) {
                  <span class="field-error">Password is required</span>
                } @else if (form.get('newPassword')?.hasError('passwordPolicy')) {
                  <span class="field-error">{{ passwordPolicyHint }}</span>
                }
              }
            </div>
            <div class="form-group">
              <label for="confirmPassword">Confirm password <span class="required">*</span></label>
              <input id="confirmPassword" type="password" formControlName="confirmPassword" placeholder="••••••••" autocomplete="new-password" />
              @if (form.get('confirmPassword')?.touched) {
                @if (form.get('confirmPassword')?.hasError('required')) {
                  <span class="field-error">Please confirm your password</span>
                } @else if (form.get('confirmPassword')?.hasError('passwordPolicy')) {
                  <span class="field-error">{{ passwordPolicyHint }}</span>
                } @else if (form.hasError('mismatch')) {
                  <span class="field-error">Passwords do not match</span>
                }
              }
            </div>
            <button type="submit" class="auth-button primary" [disabled]="form.invalid || loading">
              {{ loading ? 'Updating…' : 'Update password' }}
              <span class="btn-arrow">→</span>
            </button>
          </form>
        }

        <div class="auth-footer">
          <p>Remember your password? <a routerLink="/login" class="auth-link">Sign in</a></p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: linear-gradient(160deg, var(--scryber-rail-from) 0%, var(--scryber-rail-to) 55%, #022c22 100%);
      padding: 0 0 20px;
    }
    .auth-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 40px rgba(6, 78, 59, 0.25);
      padding: 40px;
      width: calc(100% - 40px);
      max-width: 400px;
      margin: auto;
    }
    .back-link {
      display: inline-block;
      color: var(--scryber-green-600);
      font-size: 14px;
      font-weight: 500;
      text-decoration: none;
      margin-bottom: 20px;
    }
    .back-link:hover { text-decoration: underline; }
    .auth-header h1 {
      color: #2d3748;
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 8px 0;
    }
    .auth-subtitle {
      color: #718096;
      font-size: 14px;
      margin: 0 0 24px 0;
      line-height: 1.5;
    }
    .auth-error {
      padding: 12px 14px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .auth-form { display: flex; flex-direction: column; gap: 18px; }
    .form-group { display: flex; flex-direction: column; gap: 6px; }
    .form-group label { color: #374151; font-size: 14px; font-weight: 500; }
    .required { color: #b91c1c; }
    .form-group input {
      padding: 10px 12px;
      font-size: 15px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background: white;
      color: #1f2937;
    }
    .form-group input:focus {
      outline: none;
      border-color: var(--scryber-green-600);
      box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.25);
    }
    .field-error { font-size: 12px; color: #b91c1c; }
    .auth-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: 4px;
    }
    .auth-button.primary {
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      color: white;
    }
    .auth-button.primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(5, 150, 105, 0.4);
    }
    .auth-button:disabled { opacity: 0.7; cursor: not-allowed; }
    .btn-arrow { font-size: 18px; }
    .auth-footer {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
    }
    .auth-footer p { color: #718096; font-size: 14px; margin: 0; }
    .auth-link { color: var(--scryber-green-600); font-weight: 500; text-decoration: none; }
    .auth-link:hover { text-decoration: underline; }
    @media (max-width: 480px) { .auth-card { padding: 24px; } }
  `],
})
export class ResetPasswordComponent implements OnInit {
  readonly passwordPolicyHint = PASSWORD_POLICY_HINT;
  form: FormGroup;
  token = '';
  tokenMissing = false;
  loading = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group(
      {
        newPassword: ['', [Validators.required, passwordPolicyValidator()]],
        confirmPassword: ['', [Validators.required, passwordPolicyValidator()]],
      },
      { validators: (ac: AbstractControl) => this.passwordMatch(ac as FormGroup) }
    );
  }

  private passwordMatch(g: FormGroup): ValidationErrors | null {
    const a = g.get('newPassword')?.value;
    const b = g.get('confirmPassword')?.value;
    return a && b && a !== b ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') || this.route.snapshot.fragment || '';
    if (!this.token) this.tokenMissing = true;
  }

  onSubmit(): void {
    if (this.form.invalid || this.loading || !this.token) return;
    this.errorMessage = '';
    this.loading = true;
    this.auth.resetPassword(this.token, this.form.get('newPassword')!.value).subscribe({
      next: () => this.router.navigate(['/']),
      error: (err) => {
        this.loading = false;
        const body = err && (err as any).error;
        const msg =
          (body && typeof body === 'object' && (body.message || body.error)) ||
          (typeof body === 'string' ? body : null) ||
          err?.message;
        this.errorMessage = msg && String(msg).trim() ? String(msg).trim() : 'Failed to update password. The link may have expired.';
        this.cdr.markForCheck();
      },
    });
  }
}
